import type { EnrichedStockData, StockData } from '@/types/chart';
import { OhlcvIndex } from '@/types/chart';
import { ema12, ema26, elder, rsiCalculator, fullSTO } from './chartHandlers';

/**
 * Define required window size for each indicator
 * Minimum number of candles needed to calculate each indicator
 */
const _INDICATOR_WINDOWS = {
  EMA12: 12,
  EMA26: 26,
  RSI: 15, // 14 + 1 (warmup)
  STOCH: 17, // %K(14) + %D(3)
};

/**
 * Legacy candle data format (date, open, high, low, close as separate properties)
 * This is the format expected by react-financial-charts indicator functions
 */
interface LegacyCandleData {
  date: Date;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

/**
 * Recalculate indicators for entire array
 * Using full array calculation to maintain indicator continuity
 *
 * @param candles Full candle array
 * @param startIndex Not used (kept for API compatibility)
 * @returns Full candle array with recalculated indicators
 */
export function recalculateIndicatorsIncremental(
  candles: EnrichedStockData[],
  _startIndex: number,
): EnrichedStockData[] {
  // Convert to legacy format (date, open, high, low, close as separate properties)
  // This is required by react-financial-charts indicator functions
  const legacyCandles: LegacyCandleData[] = candles.map(c => ({
    date: new Date(c.ohlcv[OhlcvIndex.TIME]),
    open: c.ohlcv[OhlcvIndex.OPEN],
    high: c.ohlcv[OhlcvIndex.HIGH],
    low: c.ohlcv[OhlcvIndex.LOW],
    close: c.ohlcv[OhlcvIndex.CLOSE],
    volume: c.ohlcv[OhlcvIndex.VOLUME],
  }));

  // Calculate indicators for entire array to maintain continuity
  // Filter out null values (important for indicator calculation chain)
  const calculated = fullSTO(rsiCalculator(elder(ema26(ema12(legacyCandles)))))
    .filter((d: unknown) => d != null)
    .map((d: unknown, index: number) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const item = d as LegacyCandleData & Record<string, unknown>;
      // Preserve volume from original candles in case indicator functions don't preserve it
      const originalVolume = candles[index]?.ohlcv[OhlcvIndex.VOLUME] || 0;
      const calculatedVolume = item.volume !== undefined ? item.volume : originalVolume;

      return {
        // Convert back to ohlcv array format
        ohlcv: [
          new Date(item.date).getTime(),
          item.open,
          item.high,
          item.low,
          item.close,
          calculatedVolume, // Use preserved volume
          0, // quoteVolume
        ] as [number, number, number, number, number, number, number],
        // Extract calculated indicators
        ema12: item.ema12,
        ema26: item.ema26,
        elder: elder.accessor()(item),
        rsi: rsiCalculator.accessor()(item),
        fullSTO: fullSTO.accessor()(item),
      };
    });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return calculated as unknown as EnrichedStockData[];
}

/**
 * Recalculate all candles (used when adding new candle)
 * Same logic as processCandles() from chartHandlers.ts
 *
 * @param candles Full candle array (can be EnrichedStockData[] or StockData[])
 * @returns Candle array with calculated indicators
 */
export function recalculateIndicatorsFull(candles: StockData[] | EnrichedStockData[]): EnrichedStockData[] {
  // Convert to legacy format
  const legacyCandles: LegacyCandleData[] = candles.map(c => ({
    date: new Date(c.ohlcv[OhlcvIndex.TIME]),
    open: c.ohlcv[OhlcvIndex.OPEN],
    high: c.ohlcv[OhlcvIndex.HIGH],
    low: c.ohlcv[OhlcvIndex.LOW],
    close: c.ohlcv[OhlcvIndex.CLOSE],
    volume: c.ohlcv[OhlcvIndex.VOLUME],
  }));

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return fullSTO(rsiCalculator(elder(ema26(ema12(legacyCandles)))))
    .filter((d: unknown) => d != null)
    .map((d: unknown, index: number) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const item = d as LegacyCandleData & Record<string, unknown>;
      const originalVolume = candles[index]?.ohlcv[OhlcvIndex.VOLUME] || 0;
      const calculatedVolume = item.volume !== undefined ? item.volume : originalVolume;

      return {
        ohlcv: [new Date(item.date).getTime(), item.open, item.high, item.low, item.close, calculatedVolume, 0] as [
          number,
          number,
          number,
          number,
          number,
          number,
          number,
        ],
        ema12: item.ema12,
        ema26: item.ema26,
        elder: elder.accessor()(item),
        rsi: rsiCalculator.accessor()(item),
        fullSTO: fullSTO.accessor()(item),
      };
    }) as unknown as EnrichedStockData[];
}
